import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SettingsLayout from "@/components/SettingsLayout";
import DataCleanupSections from "@/components/DataCleanupSections";

/**
 * Phase B5 — Org Admin "Data tools" page (/settings/data).
 *
 * Three sections (per brief B5.3):
 *   A. Quick clean — onboarding sample data card (shown only when >0).
 *   B. Clear by collection — picker + optional source-tag filter.
 *   C. Information panel — hard-delete reminder.
 *
 * Org-admin scope is forced server-side; we don't even render an org picker.
 * The brief explicitly forbids the full-org wipe from this surface.
 */
export default function DataTools() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusSample = params.get("focus") === "sample";

  if (user?.role !== "org_admin" && user?.role !== "super_admin") {
    // super_admin lands here too via Settings rail but should use the
    // dedicated console — redirect them silently for clarity.
    if (user?.role === "super_admin") {
      return (
        <SettingsLayout>
          <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-6">
            <h1 className="text-2xl font-bold text-navy-900 dark:text-white mb-2">Data tools</h1>
            <p className="text-sm text-navy-600 dark:text-navy-200 mb-4">
              As a super admin, use the cross-org Data cleanup console — it supports any org plus the full-org reset.
            </p>
            <button
              type="button"
              onClick={() => navigate("/super-admin/data-cleanup")}
              className="px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
              data-testid="data-tools-super-redirect"
            >
              Open Data cleanup console
            </button>
          </div>
        </SettingsLayout>
      );
    }
    return <SettingsLayout><div /></SettingsLayout>;
  }

  return (
    <SettingsLayout>
      <div className="space-y-6" data-testid="data-tools-page">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy-900 dark:text-white inline-flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-orange-500" /> Data tools
            </h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
              Clear test data, sample seed data, or selected operational tables. Hard delete — fully audit-logged.
            </p>
          </div>
        </header>

        <DataCleanupSections
          targetOrgId={user.org_id}
          scopeIsSuperAdmin={false}
          focusSample={focusSample}
        />

        <div className="bg-navy-50 dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-4 flex items-start gap-3" data-testid="data-tools-info">
          <ShieldCheck className="w-5 h-5 text-navy-400 mt-0.5 shrink-0" />
          <div className="text-xs text-navy-600 dark:text-navy-200 leading-relaxed">
            <strong className="text-navy-900 dark:text-white">Hard delete — recovery is not possible.</strong>
            {" "}Every action is recorded in the audit log with the executor's user id, the scope, the collections
            touched, and the exact counts deleted. <strong>Users, sites, billing, branding and the audit log itself</strong> are
            never touched by this tool — manage those from their own settings pages.
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}
